import React, { useCallback, useMemo } from 'react'
import { ChatContextSourceType } from '@trustero/trustero-api-web/lib/chatcontext/chatcontext_pb'
import {
  ChatContext,
  ChatContextSelection,
  useChatContextQuestionnaires,
} from 'src/components/Reusable/ChatContext/useChatContext.hooks'
import { MultiSelectModal } from 'src/components/Reusable/MultiSelectModal'
import {
  ContextSelectorOptionalDataContainer,
  ContextSelectorTableText,
} from '../chatContextPanel.styles'

export type QuestionnaireModalProps = {
  show: boolean
  hide: () => void
  onBack?: () => void
  context: ChatContext
  setContext: (context: ChatContext) => void
}

// QuestionnaireModal picks which previously answered questionnaires the agent
// may draw on. Selection is by request id, the same id the answered-questions
// index is keyed by on the backend, so one pick covers every answer and
// attached document of that questionnaire.
export const QuestionnaireModal = ({
  show,
  hide,
  onBack,
  context,
  setContext,
}: QuestionnaireModalProps): JSX.Element => {
  const currentSelection = useMemo(() => {
    return context[ChatContextSourceType.QUESTIONNAIRE]
  }, [context])

  const { data, isLoading, error } = useChatContextQuestionnaires()

  const items = useMemo(() => {
    return (
      data?.getQuestionnairesList()?.map((questionnaire) => ({
        id: questionnaire.getRequestId(),
        renderItems: [
          <ContextSelectorTableText
            key={`${questionnaire.getRequestId()}-name`}
          >
            {questionnaire.getName()}
          </ContextSelectorTableText>,
          <ContextSelectorOptionalDataContainer
            key={`${questionnaire.getRequestId()}-from`}
          >
            {questionnaire.getRequestingOrgName() || 'Unknown sender'}
          </ContextSelectorOptionalDataContainer>,
        ],
      })) ?? []
    )
  }, [data])

  const onCommit = useCallback(
    (newSelection: ChatContextSelection) => {
      setContext({
        ...context,
        [ChatContextSourceType.QUESTIONNAIRE]: newSelection,
      })
    },
    [context, setContext],
  )

  return (
    <MultiSelectModal
      title="Select Answered Questionnaires"
      description="The AI agent will reuse the answers and attached documents of the questionnaires you select here."
      itemType="questionnaire"
      currentSelection={currentSelection}
      onCommit={onCommit}
      show={show}
      hide={hide}
      onBack={onBack}
      isLoading={isLoading}
      items={items}
      error={error}
      numberOfColumns={2}
      gridTemplateColumnStyling="auto auto"
    />
  )
}
